import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"
import { createLogger } from "../lib/logger"
import { getI18nMessage } from "../lib/i18n_helper"
import { showError as showErrorToast } from "../lib/platform_toast"

const log = createLogger('AdImageUpload')

/**
 * Ad Image Upload Controller
 * Specialized Active Storage Direct Upload for ad wizard
 *
 * Features:
 * - Max 5 images (BusinessConstants::MAX_AD_IMAGES)
 * - Real-time progress tracking (0-100%)
 * - Image preview grid with drag reordering support
 * - Mobile-optimized touch interactions
 * - Accessibility (ARIA labels, keyboard navigation)
 *
 * Usage:
 * <div data-controller="ad-image-upload"
 *      data-ad-image-upload-url-value="/rails/active_storage/direct_uploads"
 *      data-ad-image-upload-max-files-value="5">
 *   <input type="file" multiple accept="image/*"
 *          data-ad-image-upload-target="fileInput"
 *          data-action="change->ad-image-upload#handleFileSelect">
 *   <div data-ad-image-upload-target="container"></div>
 *   <div data-ad-image-upload-target="progressContainer" class="hidden">
 *     <div data-ad-image-upload-target="progressBar"></div>
 *     <p data-ad-image-upload-target="progressText">Uploading...</p>
 *   </div>
 * </div>
 */
export default class extends Controller {
  static targets = [
    "fileInput",         // File input element
    "container",         // Image grid container
    "progressContainer", // Progress overlay container
    "progressBar",       // Progress bar element
    "progressText"       // Progress text (e.g., "2/5 uploaded")
  ]

  static values = {
    url: { type: String, default: "/rails/active_storage/direct_uploads" },
    maxFiles: { type: Number, default: 5 },
    // Default fallback: BusinessConstants::IMAGE_UPLOAD_MAX_SIZE (5MB)
    // Always pass via data-ad-image-upload-max-size-value for SSOT compliance
    maxSize: { type: Number, default: 5 * 1024 * 1024 },
    allowedFormats: { type: String, default: "jpg,jpeg,png,webp" }
  }

  connect() {
    log.info('Ad image upload connected', {
      maxFiles: this.maxFilesValue,
      maxSize: this.maxSizeValue
    })

    this.uploadedBlobs = []
    this.activeUploads = []
  }

  // ============================================================================
  // File Selection & Validation
  // ============================================================================

  handleFileSelect(event) {
    const files = Array.from(event.target.files)

    if (files.length === 0) return

    // Validate file count
    const currentCount = this.uploadedBlobs.length
    const newTotal = currentCount + files.length

    if (newTotal > this.maxFilesValue) {
      const remaining = this.maxFilesValue - currentCount
      const errorMsg = getI18nMessage(
        this.element,
        'errorsMaxFiles',
        `最多只能上傳 ${this.maxFilesValue} 張圖片。目前已有 ${currentCount} 張，還可上傳 ${remaining} 張。`,
        { max: this.maxFilesValue, current: currentCount, remaining }
      )
      this.showError(errorMsg)
      event.target.value = ""
      return
    }

    // Validate each file
    const validFiles = files.filter(file => this.validateFile(file))

    if (validFiles.length === 0) {
      event.target.value = ""
      return
    }

    // Upload valid files
    this.uploadFiles(validFiles)

    // Clear input for re-selection
    event.target.value = ""
  }

  // Validate individual file (size and format)
  validateFile(file) {
    // Check file type
    const allowedFormats = this.allowedFormatsValue.split(',')
    const formatRegex = new RegExp(`^image/(${allowedFormats.join('|')})$`, 'i')

    if (!file.type.match(formatRegex)) {
      const errorMsg = getI18nMessage(
        this.element,
        'errorsNotImage',
        `${file.name} 不是支援的圖片格式（支援：${allowedFormats.join(', ')}）`,
        { name: file.name, formats: allowedFormats.join(', ') }
      )
      this.showError(errorMsg)
      return false
    }

    // Check file size
    if (file.size > this.maxSizeValue) {
      const maxSizeMB = (this.maxSizeValue / 1024 / 1024).toFixed(1)
      const errorMsg = getI18nMessage(
        this.element,
        'errorsFileSize',
        `${file.name} 超過 ${maxSizeMB}MB 限制`,
        { name: file.name, size: maxSizeMB }
      )
      this.showError(errorMsg)
      return false
    }

    return true
  }

  // ============================================================================
  // Upload Methods
  // ============================================================================

  async uploadFiles(files) {
    // Show progress container
    this.showProgressContainer()

    // Upload files sequentially (better UX for progress tracking)
    for (let i = 0; i < files.length; i++) {
      await this.uploadFile(files[i], i + this.uploadedBlobs.length)
    }

    // Hide progress container
    this.hideProgressContainer()
  }

  uploadFile(file, index) {
    return new Promise((resolve, reject) => {
      const upload = new DirectUpload(file, this.urlValue, this)

      // Create preview container
      const previewId = `preview-${Date.now()}-${index}`
      const previewContainer = this.createPreviewElement(file, previewId)

      // Track active upload
      const uploadInfo = { file, previewId, upload }
      this.activeUploads.push(uploadInfo)

      // Start upload
      upload.create((error, blob) => {
        // Remove from active uploads
        const idx = this.activeUploads.findIndex(u => u.previewId === previewId)
        if (idx !== -1) {
          this.activeUploads.splice(idx, 1)
        }

        if (error) {
          log.error('Upload failed', { file: file.name, error })
          this.handleUploadError(previewId, error)
          reject(error)
        } else {
          log.info('Upload completed', { file: file.name, blob: blob.signed_id })
          this.handleUploadSuccess(previewId, blob)
          resolve(blob)
        }
      })
    })
  }

  // DirectUpload callback: track progress
  directUploadWillStoreFileWithXHR(request) {
    request.upload.addEventListener("progress", (event) => {
      const progress = (event.loaded / event.total) * 100

      // Update individual preview progress
      const previewId = this.activeUploads[this.activeUploads.length - 1]?.previewId
      if (previewId) {
        const progressBar = this.element.querySelector(`[data-preview-id="${previewId}"] [data-progress-bar]`)
        if (progressBar) {
          progressBar.style.width = `${progress}%`
        }
      }

      // Update overall progress
      this.updateOverallProgress()
    })
  }

  // ============================================================================
  // UI Methods
  // ============================================================================

  // Create preview element for image
  createPreviewElement(file, previewId) {
    const reader = new FileReader()

    const container = document.createElement("div")
    container.className = "relative aspect-square rounded-xl border border-charcoal-200 overflow-hidden bg-charcoal-50"
    container.dataset.previewId = previewId

    // Image preview
    const img = document.createElement("img")
    img.className = "h-full w-full object-cover"
    img.alt = file.name

    reader.onload = (e) => {
      img.src = e.target.result
    }
    reader.readAsDataURL(file)

    container.appendChild(img)

    // Progress overlay
    const progressOverlay = document.createElement("div")
    progressOverlay.className = "absolute inset-0 bg-charcoal-900/60 flex items-center justify-center"
    progressOverlay.dataset.role = "progress"

    const progressBar = document.createElement("div")
    progressBar.className = "w-3/4 h-2 rounded-full bg-charcoal-700 overflow-hidden"

    const progressFill = document.createElement("div")
    progressFill.className = "h-full bg-crust-500 transition-all duration-300"
    progressFill.style.width = "0%"
    progressFill.dataset.progressBar = ""

    progressBar.appendChild(progressFill)
    progressOverlay.appendChild(progressBar)
    container.appendChild(progressOverlay)

    // Add to container
    if (this.hasContainerTarget) {
      this.containerTarget.appendChild(container)
    }

    return container
  }

  // Handle successful upload
  handleUploadSuccess(previewId, blob) {
    const container = this.element.querySelector(`[data-preview-id="${previewId}"]`)
    if (!container) return

    // Remove progress overlay
    const progressOverlay = container.querySelector('[data-role="progress"]')
    if (progressOverlay) {
      progressOverlay.remove()
    }

    // Add success indicator
    const successBadge = document.createElement("div")
    successBadge.className = "absolute top-2 right-2 flex h-6 w-6 items-center justify-center rounded-full bg-green-600 shadow-md animate-scale-in"
    successBadge.innerHTML = `
      <svg class="h-4 w-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
      </svg>
    `
    container.appendChild(successBadge)

    // Add remove button
    const removeButton = document.createElement("button")
    removeButton.type = "button"
    removeButton.className = "absolute top-2 left-2 flex h-6 w-6 items-center justify-center rounded-full bg-red-600 text-white shadow-md active:scale-[0.97] transition-transform"
    removeButton.setAttribute("aria-label", "移除圖片")
    removeButton.innerHTML = `
      <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
      </svg>
    `
    removeButton.addEventListener('click', () => this.removeImage(previewId, blob))
    container.appendChild(removeButton)

    // Store blob
    this.uploadedBlobs.push({ previewId, blob })

    // Create hidden field for Rails
    this.createHiddenField(blob)

    // Dispatch success event
    this.element.dispatchEvent(new CustomEvent('ad-image-upload:success', {
      bubbles: true,
      detail: { blob, count: this.uploadedBlobs.length }
    }))
  }

  // Handle upload error
  handleUploadError(previewId, error) {
    const container = this.element.querySelector(`[data-preview-id="${previewId}"]`)
    if (!container) return

    // Remove progress overlay
    const progressOverlay = container.querySelector('[data-role="progress"]')
    if (progressOverlay) {
      progressOverlay.remove()
    }

    // Add error indicator
    const errorBadge = document.createElement("div")
    errorBadge.className = "absolute inset-0 flex items-center justify-center bg-red-900/80"
    errorBadge.innerHTML = `
      <div class="text-center text-white">
        <svg class="h-8 w-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
        </svg>
        <p class="text-xs">上傳失敗</p>
      </div>
    `
    container.appendChild(errorBadge)

    // Dispatch error event
    this.element.dispatchEvent(new CustomEvent('ad-image-upload:error', {
      bubbles: true,
      detail: { error }
    }))
  }

  // Remove image from UI and uploaded blobs
  removeImage(previewId, blob) {
    // Remove container from DOM
    const container = this.element.querySelector(`[data-preview-id="${previewId}"]`)
    if (container) {
      container.remove()
    }

    // Remove from uploaded blobs
    const index = this.uploadedBlobs.findIndex(item => item.previewId === previewId)
    if (index !== -1) {
      this.uploadedBlobs.splice(index, 1)
    }

    // Remove hidden field
    this.removeHiddenField(blob)

    // Dispatch remove event
    this.element.dispatchEvent(new CustomEvent('ad-image-upload:remove', {
      bubbles: true,
      detail: { blob, count: this.uploadedBlobs.length }
    }))
  }

  // ============================================================================
  // Progress Tracking
  // ============================================================================

  showProgressContainer() {
    if (this.hasProgressContainerTarget) {
      this.progressContainerTarget.classList.remove('hidden')
    }
  }

  hideProgressContainer() {
    if (this.hasProgressContainerTarget) {
      this.progressContainerTarget.classList.add('hidden')
    }
  }

  updateOverallProgress() {
    const totalFiles = this.uploadedBlobs.length + this.activeUploads.length
    const uploadedFiles = this.uploadedBlobs.length

    if (this.hasProgressTextTarget && totalFiles > 0) {
      this.progressTextTarget.textContent = `上傳中... ${uploadedFiles}/${totalFiles}`
    }
  }

  // ============================================================================
  // Hidden Fields Management
  // ============================================================================

  createHiddenField(blob) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = "ad[images][]"
    input.value = blob.signed_id
    input.dataset.blobId = blob.signed_id

    // Append to form
    if (this.fileInputTarget.form) {
      this.fileInputTarget.form.appendChild(input)
    }
  }

  removeHiddenField(blob) {
    if (!this.fileInputTarget.form) return

    const input = this.fileInputTarget.form.querySelector(
      `input[type="hidden"][name="ad[images][]"][data-blob-id="${blob.signed_id}"]`
    )

    if (input) {
      input.remove()
    }
  }

  // ============================================================================
  // Error Handling
  // ============================================================================

  showError(message) {
    showErrorToast(message)
  }
}
